import { v4 as uuidv4 } from 'uuid'

import { externalLinks } from 'utils/constants'
import { FILE_TOO_LARGE } from 'utils/constants/errors'
import { maxImageUploadBytes, maxVideoUploadBytes, videoChunkSize } from 'utils/constants/file'
import { isFile } from 'utils/files/isFile'
import { getFileUploadSignature } from 'utils/firebase'

import { NativeVideoFile } from 'types/file'
import { ImageUploadPreset, VideoUploadPreset } from 'types/fileUpload'

const baseUrl = 'https://api.cloudinary.com/v1_1/'
const cloudName = 'upswingfitness-prod'
const apiKey = '325186782832697'

const imageUploadRoute = '/image/upload'
const videoUploadRoute = '/video/upload'

const upload = async ({
  data,
  fileType,
  signature,
  hashableBody,
  headers,
}: {
  data: Blob | string | NativeVideoFile
  fileType: 'image' | 'video'
  signature: string
  hashableBody: {
    timestamp: string
    async: string
    public_id?: string
    transformation?: string
    uploadPreset?: string
  }
  headers?: {
    [key: string]: string
  }
}) => {
  const unHashedBody = {
    file: data,
    resource_type: fileType,
    api_key: apiKey,
    async: true,
  }

  const completeBody = {
    ...unHashedBody,
    ...hashableBody,
    signature,
  }

  const formData = new FormData()

  Object.keys(completeBody).forEach((key) => {
    // @ts-expect-error - FormData doesn't know about the native extensions needed for the file
    formData.append(key, completeBody[key as keyof typeof completeBody])
  })

  return fetch(
    `${baseUrl}${cloudName}${fileType === 'image' ? imageUploadRoute : videoUploadRoute}`,
    {
      method: 'POST',
      body: formData,
      headers,
    },
  ).then((response) => {
    if (response.ok === false) {
      throw new Error('Failed to upload file')
    }

    return response
  })
}

const uploadVideoInChunks = async ({
  file,
  fileType,
  signature,
  hashableBody,
}: {
  file: File
  fileType: 'image' | 'video'
  signature: string
  hashableBody: {
    timestamp: string
    async: string
    public_id?: string
    transformation?: string
    uploadPreset?: string
  }
}) => {
  interface Chunk {
    blob: Blob
    start: number
    end: number
  }

  const chunks: Chunk[] = []
  const finishedOrder: number[] = []
  let currentByte = 0

  while (currentByte < file.size) {
    const endByte = Math.min(currentByte + videoChunkSize, file.size)
    chunks.push({
      blob: file.slice(currentByte, endByte, file.type),
      start: currentByte,
      end: endByte,
    })
    currentByte = endByte
  }

  const uploadChunk = async ({ blob, start, end }: Chunk, index?: number) => {
    const headers = {
      'X-Unique-Upload-Id': hashableBody.public_id ?? uuidv4(),
      // The end value is inclusive, so needs a minus 1
      'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
    }

    const response = await upload({
      data: blob,
      fileType,
      hashableBody,
      signature,
      headers,
    })

    if (index !== undefined) {
      finishedOrder.push(index)
    }

    return response
  }

  const [firstChunk, ...additionalChunks] = chunks

  const firstResult = await uploadChunk(firstChunk)

  const results = additionalChunks.length
    ? await Promise.all(additionalChunks.map(uploadChunk))
    : [firstResult]

  // The last result contains all the information about the upload
  const lastFinishedChunkIndex = finishedOrder[finishedOrder.length - 1] || 0

  return Promise.resolve(results[lastFinishedChunkIndex])
}

export const uploadFile = async ({
  fileType,
  file,
  fileName,
  uploadPreset,
  transformation,
  uniqueId,
  signature,
  timeStamp,
}: {
  fileType: 'image' | 'video'
  file: File | string | NativeVideoFile
  fileName?: string
  uploadPreset?: ImageUploadPreset | VideoUploadPreset
  transformation?: string
  uniqueId?: string
  signature?: string
  timeStamp?: string
}) => {
  let publicId
  if (fileName) {
    publicId = fileName
  } else if (fileType === 'video' && uniqueId) {
    publicId = uniqueId
  }

  const optionalBody: { [key: string]: unknown } = {
    ...(publicId ? { public_id: publicId } : {}),
    ...(fileType === 'video'
      ? { notification_url: `${externalLinks.UPSPACE_FUNCTIONS_FULLPATH}/videoUploadComplete` }
      : {}),
    ...(transformation ? { transformation } : {}),
    ...(uploadPreset ? { upload_preset: uploadPreset } : {}),
  }

  const hashableBody = {
    timestamp: timeStamp ?? Date.now().toString(),
    async: (fileType === 'video').toString(),
    ...optionalBody,
  }

  let internalSignature = signature ?? null
  if (!signature) internalSignature = (await getFileUploadSignature(hashableBody)).data

  if (typeof internalSignature !== 'string') {
    throw new Error('Failed to generate file upload signature')
  }

  // Web upload has 'lastModified'
  if (typeof file === 'object' && 'lastModified' in file) {
    const maxUploadSize = fileType === 'image' ? maxImageUploadBytes : maxVideoUploadBytes
    if ('size' in file && file.size > maxUploadSize) {
      throw new Error(FILE_TOO_LARGE.message)
    }

    if (fileType === 'video' && isFile(file)) {
      // If native (uri), we'll have a file and not know the size, so we cant chunk it
      return uploadVideoInChunks({ file, fileType, signature: internalSignature, hashableBody })
    }
  }

  return upload({ data: file, fileType, signature: internalSignature, hashableBody })
}
